
import React from 'react';

const WeldingFumesSection: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Welding Fumes Monitoring
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                Welding processes, utilized in many industries, release a mix of metal fumes and gases that are harmful to health, necessitating monitoring to protect workers and comply with safety regulations. Substances like hexavalent chromium, manganese, lead, and aluminum found in welding fumes pose severe health risks, including respiratory and neurological disorders.
              </p>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-8">
                <h4 className="font-semibold text-orange-800 mb-4">
                  Hazardous Substances in Welding Fumes:
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center text-orange-700">
                    <i className="ri-alert-line mr-2"></i>
                    <span>Hexavalent Chromium</span>
                  </div>
                  <div className="flex items-center text-orange-700">
                    <i className="ri-alert-line mr-2"></i>
                    <span>Manganese</span>
                  </div>
                  <div className="flex items-center text-orange-700">
                    <i className="ri-alert-line mr-2"></i>
                    <span>Lead</span>
                  </div>
                  <div className="flex items-center text-orange-700">
                    <i className="ri-alert-line mr-2"></i>
                    <span>Aluminum</span>
                  </div>
                  <div className="flex items-center text-orange-700">
                    <i className="ri-alert-line mr-2"></i>
                    <span>Iron Oxide</span>
                  </div>
                  <div className="flex items-center text-orange-700">
                    <i className="ri-alert-line mr-2"></i>
                    <span>Nickel</span>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Regulations set specific permissible exposure limits (PELs) for the dangerous substances in welding fumes, mandating companies monitor these emissions to ensure workplace safety and avoid legal penalties.
              </p>
            </div>

            <div>
              <img 
                src="https://static.readdy.ai/image/83c31d00f47a619d4f0a515f0b3bc954/93c100bf7daae305d644270ac04f5203.jpeg"
                alt="Industrial Hygiene Welding Fumes Monitoring"
                className="w-full h-[400px] object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Comprehensive Welding Fumes Assessment
            </h3>
            <p className="text-gray-700 mb-12 text-center max-w-4xl mx-auto leading-relaxed">
              A comprehensive welding fumes monitoring program involves identifying metals present and assessing their concentrations during welding tasks. This data is vital for crafting strategies to control exposure and safeguard worker health.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-microscope-line text-blue-600 text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-3 text-center">Metal Identification</h4>
                <p className="text-gray-700 text-center">
                  Comprehensive analysis to identify specific metals and their concentrations in welding fumes.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-green-600 text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-3 text-center">Exposure Control</h4>
                <p className="text-gray-700 text-center">
                  Development of strategies including ventilation systems and protective equipment recommendations.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-graduation-cap-line text-purple-600 text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-3 text-center">Worker Training</h4>
                <p className="text-gray-700 text-center">
                  Education programs on health risks and preventive measures to enhance safety protocols.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-gray-700 mb-8 leading-relaxed">
                At Safety Consultants USA, we stress the importance of exposure control strategies and employee education to mitigate risks associated with welding fumes. Recommendations include installing local exhaust ventilation, using alternative materials, and ensuring workers wear personal protective equipment.
              </p>
              <a 
                href="#contact-form" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule Welding Assessment
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WeldingFumesSection;
